import api from '../api/api';
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';
import { getPlanFromToken, isTokenExpired, tokenExpiresSoon } from '../utils/jwtUtils';


const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const initialToken = localStorage.getItem('token');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(initialToken);
  const [plan, setPlan] = useState('free');

  useEffect(() => {
    if (token) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        logout();
        return;
      }

      // Extract plan from token
      const tokenPlan = getPlanFromToken(token);
      setPlan(tokenPlan);

      // Check if token needs refresh
      if (tokenExpiresSoon(token)) {
        refreshToken();
      } else {
        loadUser();
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  // 文字サイズをCSS変数に適用
  const applyFontSize = (size) => {
    const fontSizeMap = {
      small: '14px',
      normal: '16px',
      large: '18px',
      xlarge: '20px'
    };
    document.documentElement.style.setProperty('--font-size-base', fontSizeMap[size] || '16px');
  };

  const loadUser = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData.user);
      setPlan(userData.user.plan);

      // Phase 2-A: ユーザー設定（文字サイズ）を適用
      if (userData.user.settings?.fontSize) {
        applyFontSize(userData.user.settings.fontSize);
      }

      // Ensure token is in sync with user plan
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        const tokenPlan = getPlanFromToken(currentToken);
        if (tokenPlan !== userData.user.plan) {
          await refreshToken();
        }
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authService.refreshToken();
      setToken(response.token);
      setUser(response.user);
      setPlan(response.user.plan);
      localStorage.setItem('token', response.token);
    } catch (error) {
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authService.login(email, password);

      api.defaults.headers.common.Authorization = `Bearer ${response.token}`;

      setToken(response.token);
      setUser(response.user);
      setPlan(response.user.plan);

      localStorage.setItem('token', response.token);

      setLoading(false);

      return response;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const register = async (email, password, selectedPlan = 'free') => {
    const response = await authService.register(email, password, selectedPlan);

    // ✅ 保存順を必ず守る
    localStorage.setItem('token', response.token); // これが先
    setToken(response.token); // 次に状態反映
    setUser(response.user);
    setPlan(response.user.plan);

    // Ensure api headers are updated before any subsequent calls
    api.defaults.headers.common.Authorization = `Bearer ${response.token}`;
    api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;
    
    // Force a small delay to ensure header propagation
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 🔽 念のためこれを追加：即座に loadUser() を呼んで確認させる
    await loadUser();
    return response;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPlan('free');
    localStorage.removeItem('token');
    delete api.defaults.headers.common.Authorization;
    delete api.defaults.headers.common['Authorization'];
  };

  const updatePlan = async (newPlan) => {
    const response = await authService.updatePlan(newPlan);
    
    // Update token if new one is provided (plan change generates new JWT)
    if (response.token) {
      setToken(response.token);
      localStorage.setItem('token', response.token);
      api.defaults.headers.common.Authorization = `Bearer ${response.token}`;
    }
    
    setUser(response.user);
    setPlan(response.user.plan);
    
    // Force a token refresh to ensure everything is in sync
    await refreshToken();
    
    return response;
  };

  // Update user data (e.g., after profile update)
  const updateUser = (updatedUserData) => {
    if (!updatedUserData) return;
    
    // Merge updated data with existing user data
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData,
      // Ensure important fields are preserved or updated correctly
      isEmailVerified: updatedUserData.isEmailVerified !== undefined 
        ? updatedUserData.isEmailVerified 
        : prevUser?.isEmailVerified
    }));
    
    // Update plan if changed
    if (updatedUserData.plan) {
      setPlan(updatedUserData.plan);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updatePlan,
    updateUser,
    refreshToken,
    isAuthenticated: !!user,
    plan: plan, // Use plan from JWT token
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};